import { useCallback, useState } from "react"
import { Link } from "react-router-dom"

import { GripVertical, Pencil } from "lucide-react"
import { Badge, DefaultTable, ButtonIcon, Button, DefaultPopover, Separator, DefaultTooltip, cn } from "twyne-new.ui"

import { DndSortableList, useSortableDndListItem } from "@/components/SortableDnd"
import { DeleteIconConfirmation } from "@/components/table"
import { useAppAlert } from "@Components/AppAlert"
import TextTruncated from "@Components/TextTruncated"
import { PureSwitch } from "@Components/form/Switch"

import { useRewardStore } from "../store"

function PagesCell({ pages }) {
  const [open, setOpen] = useState(false)

  if (!pages?.length) return '--'

  return <DefaultPopover
    trigger={
      <Button size='sm' variant='outline' color='neutral' onClick={() => setOpen(!open)}>
        {pages.length} page{pages.length > 1 ? 's' : ''}
      </Button>
    }
    wrapperProps={{ open, onOpenChange: setOpen }}
  >
    <p className='font-semibold mb-1'>Pages</p>
    <Separator className='my-2' />
    <ul className='flex flex-col gap-1'>
      {pages.map(({ id, name }) => <li key={id}>
        <Link to={`/path/pages/${id}`} className='twy-text-accent'>
          <strong>[#{id}]</strong> {name}
        </Link>
      </li>)}
    </ul>
  </DefaultPopover>
}

function SortableTableRow({ children, row, className }) {
  const dnd = useSortableDndListItem(row.id)

  return <tr
    ref={dnd.setNodeRef}
    style={dnd.style}
    className={className}
  >
    {children}
  </tr>
}

function Steps({ handleRefresh, onMove, onReorder, rewardSteps = [], type, canReorder = true }) {
  const { deleteStep, updateStepActive } = useRewardStore()
  const { handleSuccess, handleError } = useAppAlert()

  const handleDelete = async id => {
    try {
      await deleteStep(id)
      await handleRefresh()
      handleSuccess('Success', 'Step deleted.')
    } catch (error) {
      handleError('Error', 'Step was not deleted.')
    }
    await handleRefresh()
  }

  const handleUpdateActive = async ({ id, active }) => {
    try {
      await updateStepActive(id, !active)
      await handleRefresh()
      handleSuccess('Success', 'Step updated.')
    } catch (error) {
      handleError('Error', 'Step was not updated.')
    }
  }

  const onSortEnd = useCallback(newOrder => {
    const next = newOrder.map((step, index) => ({ ...step, order: index }))
    onReorder(next)
  }, [onReorder])

  const columns = [
    {
      dataField: '',
      formatter: (_, row) => <DefaultTooltip content={!canReorder && 'You cannot reorder steps because filtering is enabled.'}>
        <div className={cn(!canReorder && 'opacity-50 pointer-events-none')}>
          <DragFormatter row={row} disabled={!canReorder} />
        </div>
      </DefaultTooltip>,
      text: "",
    },
    {
      dataField: 'id',
      text: 'Id',
      sort: true,
    },
    {
      dataField: 'name',
      text: 'Name',
      formatter: (cell, row) => <>
        <div className='twy-text-base-accent'>
          <TextTruncated maxLength={45} id={`name-step-${row.id}`} placement='bottom'>
            {row.name}
          </TextTruncated>
        </div>
        <span>
          <span className='text-sm'>
            <TextTruncated maxLength={45} id={`description-step-${row.id}`} placement='bottom'>
              {row.description}
            </TextTruncated>
          </span>
        </span>
      </>,
    },
    {
      dataField: 'slug',
      text: 'Slug',
      formatter: cell => cell || '--',
    },
    {
      dataField: 'mode',
      text: 'Mode',
      formatter: cell => <Badge variant='light' color={cell === 'progression' ? 'accent' : 'neutral'} size='sm'>{cell}</Badge>,
    },
    {
      dataField: 'points',
      text: !['deals_only', 'master_cashout'].includes(type) ? 'Points' : 'Order',
      formatter: (_, row) => type === 'points' ? row.points : row.order + 1,
    },
    {
      dataField: 'offersRequired',
      text: 'Offers Required',
      formatter: cell => cell || '--',
    },
    {
      dataField: 'pages',
      text: 'Pages',
      formatter: cell => <PagesCell pages={cell} />,
    },
    {
      dataField: 'active',
      text: 'Active',
      formatter: (cell, row) => <PureSwitch value={row.active} onChange={() => handleUpdateActive(row)} />,
    },
    {
      dataField: 'action',
      text: '',
      className: 'w-8',
      formatter: (cell, row) => <div className='flex gap-2 border-l twy-border-base-accent pl-2'>
        <ButtonIcon content='Edit'>
          <Link to={`/cms/rewards/${row.rewardId}/steps/${row.id}`}>
            <Pencil size={16} />
          </Link>
        </ButtonIcon>
        <ButtonIcon content='Delete'>
          <DeleteIconConfirmation id={`delete-${row.id}`} handleClick={() => handleDelete(row.id)} />
        </ButtonIcon>
      </div>,
    },
  ]

  return <>
    <DndSortableList items={rewardSteps} onSortEnd={onSortEnd}>
      <DefaultTable
        columns={columns}
        data={rewardSteps}
        rowWrapper={props => <SortableTableRow {...props} onMove={onMove} />}
        keyField="id"
        className="max-h-[67vh] overflow-y-auto overflow-x-hidden pr-2 mr-[-4px]"
      />
    </DndSortableList>
  </>
}

const DragFormatter = ({ row }) => {
  const { dragProps } = useSortableDndListItem(row.id)
  return <ButtonIcon {...dragProps}>
    <GripVertical size={15} />
  </ButtonIcon>
}

export default Steps
